import { toast, Toaster, type Toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, Info, Loader2, X } from 'lucide-react'
import { cn } from '@utils/cn'

const TONE = {
  success: { box: 'border-success/40 bg-success-soft text-success', icon: CheckCircle2 },
  error: { box: 'border-danger/40 bg-danger-soft text-danger', icon: AlertCircle },
  blank: { box: 'border-border bg-surface-raised text-fg', icon: Info },
  custom: { box: 'border-border bg-surface-raised text-fg', icon: Info },
  loading: { box: 'border-border bg-surface-raised text-fg', icon: Loader2 },
} as const

function AppToast({ t }: { t: Toast }) {
  const { t: tr } = useTranslation('common')
  const tone = TONE[t.type] ?? TONE.blank
  const Icon = tone.icon
  const timed = t.type !== 'loading' && Number.isFinite(t.duration)
  return (
    <div
      role={t.type === 'error' ? 'alert' : 'status'}
      className={cn('app-toast relative w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border shadow-lg', tone.box, t.visible ? 'app-toast-in' : 'app-toast-out')}
    >
      <div className="flex items-start gap-3 py-3 pl-3.5 pr-2">
        <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', t.type === 'loading' && 'animate-spin')} aria-hidden="true" />
        <div className="min-w-0 flex-1 break-words text-sm font-medium leading-snug">{typeof t.message === 'function' ? t.message(t) : t.message}</div>
        <button type="button" onClick={() => toast.dismiss(t.id)} aria-label={tr('toast.close')} title={tr('toast.close')}
          className="shrink-0 rounded-lg p-1 opacity-70 transition hover:bg-black/10 hover:opacity-100">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {/* Shrinks over the time the message stays; it pauses while the pointer is on the message, as the timer does. */}
      {timed && <div aria-hidden="true" className="h-1 w-full bg-black/10"><div className="app-toast-bar h-full origin-left bg-current opacity-70" style={{ animationDuration: `${t.duration}ms` }} /></div>}
    </div>
  )
}

/** Every message in the app goes through here: icon, text, a close button, and a bar showing how long is left. */
export function AppToaster() {
  return (
    <Toaster position="top-right" toastOptions={{ duration: 4000, success: { duration: 3500 }, error: { duration: 6000 } }}>
      {(t) => <AppToast t={t} />}
    </Toaster>
  )
}
